import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import type { WorkbookInstance } from '@fortune-sheet/react';
import type { FortuneSheet } from './types';

/**
 * Above this many total cells, non-active sheets mount as stubs and hydrate
 * right after first paint. Fortune-Sheet eagerly expands EVERY sheet's celldata
 * into a dense row×column matrix (plus a formula-map pass) at mount, so a large
 * multi-tab workbook pays the full cost before anything renders.
 */
export const LAZY_HYDRATION_CELL_THRESHOLD = 50_000;

// Stub grid dimensions — small enough that the eager dense-matrix expansion of
// a stub costs nothing. Real dimensions arrive with hydration (updateSheet
// expands to the payload's row/column).
const STUB_ROWS = 50;
const STUB_COLS = 26;

// Hydration cadence: wait a beat after mount so the first paint lands, then
// fill one sheet per tick so no single task blocks input for long.
const FIRST_HYDRATE_DELAY_MS = 120;
const HYDRATE_TICK_MS = 30;

const totalCells = (sheets: FortuneSheet[]): number => sheets.reduce((n, s) => n + (s.celldata?.length ?? 0), 0);

/** Exported for tests. Keeps id/name/order/config (styling metadata is cheap and
 *  survives hydration — updateSheet's celldata path only swaps cell content in),
 *  drops the cell payload, and shrinks the grid to stub dimensions. */
export const makeStubSheet = (sheet: FortuneSheet): FortuneSheet => ({
    ...sheet,
    celldata: [],
    row: STUB_ROWS,
    column: STUB_COLS,
});

export interface UseLazySheetsArgs {
    /** Fully-resolved sheets (celldata form). */
    sheets: FortuneSheet[] | null | undefined;
    workbookRef: RefObject<WorkbookInstance>;
    /** Test seam; defaults to LAZY_HYDRATION_CELL_THRESHOLD. */
    cellThreshold?: number;
    /**
     * Set to `true` for the duration of each hydration `updateSheet` call, then
     * back to `false` — `updateSheet` goes through the same `onOp`/emitOp path a
     * real edit does, and a stub-fill's ops are path-identical to a real edit
     * (both write into `data`/`celldata`), so the caller's dirty-tracking (e.g.
     * markWorkbookDirty) reads this ref to tell the two apart. Owned by the
     * caller; a hook-local ref is used when none is passed (read-only viewers
     * with no dirty-tracking, existing tests).
     */
    suppressDirtyRef?: MutableRefObject<boolean>;
}

/**
 * Deferred per-sheet hydration for large workbooks. Below the threshold this is
 * a pass-through. Above it, only the active sheet mounts with content; the
 * other tabs mount as empty stubs, and `hydratePending` (called by the host
 * component right after the Workbook mounts) fills them in one per tick via
 * workbookRef.updateSheet(). Deliberately NOT hooked to tab activation — the
 * first shipped version hydrated on Fortune-Sheet's afterActivateSheet hook,
 * which left every stub blank whenever activation semantics didn't line up
 * (e.g. no sheet carried status === 1, so OUR stubbing and the library's
 * ensureSheetIndex disagreed about which sheet was active).
 *
 * Callers that read sheet content back out (save/download/add-to-reports) MUST
 * still route getAllSheets() through `getFullSheets` — in the brief window
 * before hydration completes, a stub persisted raw would wipe that sheet.
 */
export const useLazySheets = ({
    sheets,
    workbookRef,
    cellThreshold = LAZY_HYDRATION_CELL_THRESHOLD,
    suppressDirtyRef: suppressDirtyRefProp,
}: UseLazySheetsArgs) => {
    // Sheets awaiting hydration for the last COMMITTED render, keyed by sheet
    // id. Written only from the effect below — never from useMemo — so a
    // render React replays or discards can never mutate the queue a committed
    // workbook already has a drain scheduled against (see pendingMap).
    const pendingRef = useRef<Map<string, FortuneSheet>>(new Map());
    const ownSuppressDirtyRef = useRef(false);
    const suppressDirtyRef = suppressDirtyRefProp ?? ownSuppressDirtyRef;

    const { workbookSheets, pendingMap } = useMemo(() => {
        if (!sheets || sheets.length < 2 || totalCells(sheets) <= cellThreshold) {
            return { workbookSheets: sheets, pendingMap: new Map<string, FortuneSheet>() };
        }
        // Mirror Fortune-Sheet's ensureSheetIndex: when no sheet carries
        // status === 1, the library activates the FIRST sheet at mount — so
        // that is the one sheet that must never be a stub. Stubbing by
        // `status !== 1` alone blanked the whole workbook for saved/composed
        // sheet arrays where every status was 0.
        const statusActive = sheets.findIndex((s) => Number(s.status) === 1);
        const activeIndex = statusActive === -1 ? 0 : statusActive;
        const pending = new Map<string, FortuneSheet>();
        const stubbed = sheets.map((sheet, i) => {
            if (i === activeIndex) return sheet;
            pending.set(String(sheet.id), sheet);
            return makeStubSheet(sheet);
        });
        return { workbookSheets: stubbed, pendingMap: pending };
    }, [sheets, cellThreshold]);

    // Publish the derived map only once this render has committed. A render
    // React discards (StrictMode double-render, an abandoned concurrent
    // render) still runs the useMemo above but never reaches this effect, so
    // pendingRef always reflects the committed workbook.
    useEffect(() => {
        pendingRef.current = pendingMap;
    }, [pendingMap]);

    /**
     * Drain all pending stubs into the live workbook, one sheet per tick.
     * Idempotent and safe to call repeatedly (StrictMode double-effects): each
     * sheet is removed from the queue before it is applied. Captures the
     * queue once per call — if a later commit publishes a new map before this
     * drain finishes, `pendingRef.current` no longer matches the captured
     * `queue` and the stale drain stops instead of touching the new workbook.
     */
    const hydratePending = useCallback(() => {
        const queue = pendingRef.current;
        const drain = () => {
            const wb = workbookRef.current;
            if (!wb || pendingRef.current !== queue || queue.size === 0) return;
            const next = queue.entries().next();
            if (next.done) return;
            const [id, sheet] = next.value;
            queue.delete(id);
            // celldata path of updateSheet: expands to the payload's
            // row/column and registers formulas sparsely, keeping the stub's
            // config (borders/merges/widths) intact. Suppressed synchronously
            // around the call — updateSheet emits onOp ops path-identical to a
            // real edit (see suppressDirtyRef above).
            suppressDirtyRef.current = true;
            try {
                wb.updateSheet([sheet]);
            } finally {
                suppressDirtyRef.current = false;
            }
            if (queue.size) setTimeout(drain, HYDRATE_TICK_MS);
        };
        if (queue.size) setTimeout(drain, FIRST_HYDRATE_DELAY_MS);
    }, [workbookRef, suppressDirtyRef]);

    /** Replace still-stubbed live sheets with their full pending originals. */
    const getFullSheets = useCallback((live: FortuneSheet[]): FortuneSheet[] => {
        if (!pendingRef.current.size) return live;
        return live.map((sheet) => pendingRef.current.get(String(sheet.id)) ?? sheet);
    }, []);

    return { workbookSheets, getFullSheets, hydratePending };
};
